import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, Sparkles, Lock } from "lucide-react";
import { track } from "../lib/analytics";
import PrivacyAssuranceLink from "./PrivacyAssuranceLink";

export default function Hero() {
  return (
    <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
      {/* soft radial background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 600px at 50% 0%, rgba(72,106,143,0.12), transparent 60%), radial-gradient(800px 500px at 80% 40%, rgba(229,214,178,0.35), transparent 60%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur-sm border border-[#e6dcc6] fade-in">
          <span className="w-1.5 h-1.5 rounded-full bg-[#486a8f] pulse-dot" />
          <Sparkles size={13} className="text-[#486a8f]" />
          <span className="font-body text-[12px] text-[#2b3442] tracking-wide">
            Adaptive AI mentor · personal roadmap
          </span>
        </div>

        <h1 className="font-display mt-8 text-[36px] sm:text-[58px] md:text-[76px] leading-[1.05] tracking-tight text-[#0f1724] fade-up">
          Your AI mentor for the
          <br />
          <span className="italic font-light text-[#486a8f]">
            next right step.
          </span>
        </h1>

        <p
          className="font-body mt-6 md:mt-8 text-[16px] md:text-[19px] text-[#4b5563] max-w-2xl mx-auto leading-relaxed fade-up px-2"
          style={{ animationDelay: "0.1s" }}
        >
          Ascend Higher helps you get unstuck, organize your thoughts, and build momentum with a personal AI mentor that listens, guides, and helps you know what to do next.
        </p>

        <div
          className="mt-8 md:mt-10 flex flex-col items-center gap-4 fade-up"
          style={{ animationDelay: "0.2s" }}
        >
          <Link to="/start" onClick={() => track("hero_cta_click")}>
            <Button data-testid="hero-start-trial-btn" className="group h-14 px-8 md:px-9 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[15px] md:text-[16px] btn-lift">
              Start My 3-Day Trial
              <ArrowRight
                size={17}
                className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
              />
            </Button>
          </Link>
          <p className="font-body text-[12px] text-[#6b7280] max-w-md text-center leading-relaxed">
            Not therapy. Not crisis support. Practical AI mentorship for clarity, goals, and life direction.
          </p>
          <div className="flex items-center gap-2 text-[#6b7280]">
            <Lock size={11} />
            <p className="font-body text-[13px]">
              Free 3-day trial · Card required · Cancel anytime · Secure checkout by Stripe
            </p>
          </div>
          <PrivacyAssuranceLink />
          <Link to="/challenge" className="font-body text-[13px] text-[#486a8f] underline hover:text-[#3a5a7c] mt-1">
            Or start the free 7-day reset →
          </Link>
        </div>

        {/* subtle preview card */}
        <div
          className="mt-20 max-w-3xl mx-auto fade-up"
          style={{ animationDelay: "0.35s" }}
        >
          <div className="relative rounded-2xl bg-white border border-[#e9e0cd] shadow-[0_30px_80px_-40px_rgba(15,23,36,0.3)] overflow-hidden">
            <div className="flex items-center gap-1.5 px-5 py-3 border-b border-[#eee5d0] bg-[#faf6ec]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#e9d8a6]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#e6dcc6]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#cfd6df]" />
              <span className="ml-3 font-mono-eyebrow text-[#6b7280]">
                ascend · daily check-in
              </span>
            </div>
            <div className="p-8 text-left grid md:grid-cols-[1fr_auto] gap-6 items-start">
              <div>
                <p className="font-mono-eyebrow text-[#486a8f] mb-3">Today's focus</p>
                <h3 className="font-display text-[26px] md:text-[30px] text-[#0f1724] leading-tight">
                  Clarify the one next step that actually moves things forward.
                </h3>
                <div className="mt-5 flex flex-wrap gap-2">
                  {["Goals", "Habits", "Health", "Money", "Relationships"].map(
                    (t) => (
                      <span
                        key={t}
                        className="px-3 py-1 rounded-full bg-[#f2ecdc] text-[#486a8f] text-[12px] font-medium border border-[#e9e0cd]"
                      >
                        {t}
                      </span>
                    )
                  )}
                </div>
              </div>
              <div className="rounded-xl bg-[#f7f3ea] p-5 border border-[#eee5d0] min-w-[200px]">
                <p className="font-mono-eyebrow text-[#6b7280] mb-2">Streak</p>
                <p className="font-display text-[38px] text-[#0f1724] leading-none">7</p>
                <p className="font-body text-[12px] text-[#6b7280] mt-1">days in a row</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
